/* FJBC recently viewed. Only handles and visit times stay in this browser, for up to 30 days.
   Cards come fresh from Shopify with the shared availability rules. No account or third party.

   Two kinds of mount read the one history:
   - the collection pages' rail (snippets/recently-viewed.liquid): its own arrows, rail cards;
   - the product page band (sections/recently-viewed-band.liquid, product page C round 5): the
     anchor names its card section (data-recent-section), its card selector
     (data-recent-card-selector) and the handle to leave out (data-recent-exclude, the current
     product); its arrows belong to the related-band script, which is told after each render.
   A handle is dropped from the history only when a response carries the band card section's
   data-recent-missing marker (a 404 body is read too); a bare 404 or a failed request never drops
   one. The collection rail's card section prints no marker, so that path never drops handles,
   as before. */
(function () {
  'use strict';
  var script = document.querySelector('[data-recently-viewed-script]');
  if (!script || script.dataset.initialized) return;
  script.dataset.initialized = 'true';
  var KEY = 'fjbc-recently-viewed-v1';
  var LIMIT = 8;
  var MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  var mounts = new Map();
  var consentReady = false;
  var root = script.dataset.root || '/';
  if (!root.endsWith('/')) root += '/';
  var validHandle = function (handle) {
    return typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);
  };
  function allowed() {
    try { return consentReady && window.Shopify.customerPrivacy.preferencesProcessingAllowed() === true; }
    catch (_) { return false; }
  }
  function clearHistory() { try { localStorage.removeItem(KEY); } catch (_) {} }

  function read() {
    if (!allowed()) return [];
    try {
      var entries = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(entries)) return [];
      var now = Date.now(), seen = new Set();
      return entries.filter(function (entry) {
        if (!entry || !validHandle(entry.handle) || !Number.isFinite(entry.viewedAt) ||
            entry.viewedAt > now || now - entry.viewedAt >= MAX_AGE) return false;
        return true;
      }).sort(function (a, b) { return b.viewedAt - a.viewedAt; }).filter(function (entry) {
        if (seen.has(entry.handle)) return false;
        seen.add(entry.handle);
        return true;
      }).slice(0, LIMIT);
    } catch (_) { return []; }
  }

  function forget(handle) {
    if (!allowed()) return;
    try {
      var entries = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(entries)) return;
      localStorage.setItem(KEY, JSON.stringify(entries.filter(function (entry) {
        return !entry || entry.handle !== handle;
      })));
    } catch (_) {}
  }

  function remember() {
    if (!allowed()) return;
    var handle = script.dataset.productHandle;
    if (!validHandle(handle)) return;
    var entries = read().filter(function (entry) { return entry.handle !== handle; });
    entries.unshift({ handle: handle, viewedAt: Date.now() });
    try { localStorage.setItem(KEY, JSON.stringify(entries.slice(0, LIMIT))); } catch (_) {}
  }

  async function fetchCard(handle, signal, sectionId, selector) {
    var controller = new AbortController();
    var abort = function () { controller.abort(); };
    signal.addEventListener('abort', abort, { once: true });
    var timeout = setTimeout(abort, 7000);
    try {
      if (signal.aborted) return null;
      var url = new URL(root + 'products/' + encodeURIComponent(handle), location.origin);
      url.searchParams.set('section_id', sectionId || 'recently-viewed-card');
      var response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      // Read the body whatever the status: only the card section's own marker drops a handle, never
      // a bare 404, a server error or a network failure (those keep the history as it is).
      var doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (doc.querySelector('[data-recent-missing]')) { forget(handle); return null; }
      if (!response.ok) return null;
      var wrapper = doc.querySelector('[data-recent-card]');
      if (!wrapper || wrapper.dataset.recentCard !== handle) return null;
      var card = wrapper.querySelector(selector || '.pcard-rail');
      return card ? document.importNode(card, true) : null;
    } catch (_) { return null; }
    finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
  }

  function mount(anchor) {
    if (mounts.has(anchor)) return;
    var section = anchor.querySelector('[data-recently-viewed]');
    var rail = anchor.querySelector('[data-recent-rail]');
    var arrows = anchor.querySelector('[data-recent-arrows]');
    var previous = anchor.querySelector('[data-recent-scroll="-1"]');
    var next = anchor.querySelector('[data-recent-scroll="1"]');
    var lifetime = new AbortController(), request, observer, resize, near = false, version = 0;
    var sectionId = anchor.dataset.recentSection, selector = anchor.dataset.recentCardSelector;
    var exclude = anchor.dataset.recentExclude;

    function syncArrows() {
      if (!arrows) {
        var band = rail.closest('.pd-related');
        if (band) band.dispatchEvent(new CustomEvent('pd-rel:swapped'));
        return;
      }
      var maximum = rail.scrollWidth - rail.clientWidth;
      arrows.hidden = maximum < 2;
      previous.disabled = rail.scrollLeft < 2;
      next.disabled = rail.scrollLeft >= maximum - 2;
    }

    async function refresh() {
      if (!allowed()) {
        ++version;
        if (request) request.abort();
        rail.replaceChildren(); section.hidden = true; syncArrows();
        return;
      }
      if (!near) return;
      var current = ++version;
      if (request) request.abort();
      request = new AbortController();
      var signal = request.signal, cards = [];
      // A product page band leaves out the current product and shows at most seven.
      var entries = read();
      if (exclude) entries = entries.filter(function (entry) { return entry.handle !== exclude; }).slice(0, LIMIT - 1);
      // Three concurrent requests at most; retain visit order regardless of response order.
      for (var offset = 0; offset < entries.length && !signal.aborted; offset += 3) {
        var batch = await Promise.all(entries.slice(offset, offset + 3).map(function (entry) {
          return fetchCard(entry.handle, signal, sectionId, selector);
        }));
        cards.push.apply(cards, batch.filter(Boolean));
      }
      if (signal.aborted || current !== version || !anchor.isConnected || !allowed()) return;
      rail.replaceChildren.apply(rail, cards);
      section.hidden = cards.length === 0;
      rail.scrollLeft = 0;
      syncArrows();
    }

    anchor.addEventListener('click', function (event) {
      var button = event.target.closest('[data-recent-scroll]');
      if (!button || button.disabled || !arrows) return;
      rail.scrollBy({ left: Number(button.dataset.recentScroll) * rail.clientWidth * .85,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, { signal: lifetime.signal });
    rail.addEventListener('scroll', syncArrows, { passive: true, signal: lifetime.signal });
    if ('ResizeObserver' in window) { resize = new ResizeObserver(syncArrows); resize.observe(rail); }
    else window.addEventListener('resize', syncArrows, { signal: lifetime.signal });
    mounts.set(anchor, { refresh: refresh, destroy: function () {
      lifetime.abort(); if (request) request.abort();
      if (observer) observer.disconnect(); if (resize) resize.disconnect();
      mounts.delete(anchor);
    } });
    /* Load when the browser is first idle after the page loads, or when the band comes within 800px,
       whichever is first, so the cards are not fetched and inserted while the visitor is scrolling
       onto the band (Eli felt a short scroll pause there, 2026-10-05). */
    var start = function () {
      if (near || lifetime.signal.aborted) return;
      if (observer) observer.disconnect();
      near = true; refresh();
    };
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) start();
      }, { rootMargin: '800px' });
      observer.observe(anchor);
    }
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
    if (document.readyState === 'complete') idle(start, { timeout: 3000 });
    else window.addEventListener('load', function () { idle(start, { timeout: 3000 }); }, { once: true, signal: lifetime.signal });
  }

  function init() {
    mounts.forEach(function (mount, anchor) { if (!anchor.isConnected) mount.destroy(); });
    document.querySelectorAll('[data-recently-viewed-anchor]').forEach(mount);
  }
  init();
  function syncConsent(clearDenied) {
    if (allowed()) remember();
    else if (consentReady || clearDenied === true) clearHistory();
    mounts.forEach(function (mount) { mount.refresh(); });
  }
  document.addEventListener('visitorConsentCollected', function () { syncConsent(true); });
  syncConsent();
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      consentReady = !error;
      syncConsent(true);
    });
  }
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) { init(); syncConsent(); }
  });
  window.addEventListener('storage', function (event) {
    if (event.key === KEY || event.key === null) mounts.forEach(function (mount) { mount.refresh(); });
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    mounts.forEach(function (mount, anchor) { if (event.target.contains(anchor)) mount.destroy(); });
  });
}());
